/**
 * Generic "WhatsApp any report" button.
 *
 * Wraps the same html-to-image + wa.me flow used by the biometric
 * growth-chart share button, but takes a `reportLabel` + `getMessageLines`
 * so it can be dropped onto any card in the app (latest test snapshot,
 * athlete trend chart, marker trends, lab record, response tracker
 * card, advisory panel, …).
 *
 * Usage:
 *   <WhatsAppShareReportButton
 *     getNode={() => snapshotRef.current}
 *     filename={`${athleteName}-latest-snapshot.png`}
 *     athleteName={athleteName}
 *     reportLabel="latest test snapshot"
 *     getMessageLines={() => [`Test date: ${latest.date}`, "Full snapshot attached — please scroll."]}
 *     familyContacts={sailor.family_contacts}
 *   />
 *
 * Why not send the image auto-magically? `wa.me` deep links carry
 * text only. Sending media programmatically needs the WhatsApp
 * Business API (Twilio / Meta Cloud) which isn't provisioned yet.
 * Downloading the PNG + pre-filling the text is the best UX possible
 * without API keys.
 */
import React, { useState } from "react";
import { toPng } from "html-to-image";
import { MessageCircle, ChevronDown, User, Users, UserRound } from "lucide-react";
import { alertDialog } from "../utils/dialogs";

const ROLE_META = {
  guardian: { label: "Guardian", icon: Users,      color: "#06B6D4" },
  mother:   { label: "Mother",   icon: UserRound,  color: "#F472B6" },
  father:   { label: "Father",   icon: User,       color: "#38BDF8" },
  other:    { label: "Contact",  icon: User,       color: "#94A3B8" },
};
const ROLE_ORDER = ["guardian", "mother", "father", "other"];

function openWhatsAppTo(phone, message) {
  const num = String(phone || "").replace(/\D+/g, "");
  const withCC = num.length === 10 ? `91${num}` : num;
  const url = `https://wa.me/${withCC}?text=${encodeURIComponent(message)}`;
  window.open(url, "_blank", "noopener");
}

function openWhatsAppPicker(message) {
  const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
  window.open(url, "_blank", "noopener");
}

async function captureNodeAsPng(node, filename) {
  if (!node) throw new Error("No report node to capture");
  node.setAttribute("data-print-light", "true");
  document.body.setAttribute("data-print-light", "true");
  try {
    await new Promise((r) => requestAnimationFrame(r));
    const dataUrl = await toPng(node, {
      backgroundColor: "#FFFFFF",
      pixelRatio: 2,
      cacheBust: true,
      fontEmbedCSS: "",
      filter: (el) => !(el?.dataset && el.dataset.noprint === "true"),
    });
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    a.click();
  } finally {
    node.removeAttribute("data-print-light");
    document.body.removeAttribute("data-print-light");
  }
}

export default function WhatsAppShareReportButton({
  getNode,
  filename,
  athleteName,
  reportLabel = "report",
  getMessageLines,
  familyContacts = [],
  size = "sm",
  variant = "emerald",
  label = "WhatsApp",
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const buildMessage = () => {
    const lines = [];
    lines.push(`Hi, sharing ${athleteName}'s ${reportLabel} from the Yacht Club of Hyderabad:`);
    lines.push("");
    const custom = (typeof getMessageLines === "function" ? getMessageLines() : []) || [];
    custom.forEach((l) => l && lines.push(l));
    if (custom.length > 0) lines.push("");
    lines.push("(Image attached — please open the photo.)");
    return lines.join("\n");
  };

  const contacts = (familyContacts || [])
    .filter((c) => c && c.mobile)
    .map((c) => ({ ...c, role: (c.role || "other").toLowerCase() }))
    .sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role));

  const doShare = async (opener) => {
    if (busy) return;
    setBusy(true);
    const message = buildMessage();
    try {
      try {
        await captureNodeAsPng(getNode?.(), filename || `${athleteName}-${reportLabel}.png`);
      } catch (err) {
        console.error("Report capture failed:", err);
        alertDialog(
          "Could not save the report image, but WhatsApp will still open " +
          "with the message pre-filled. You can screenshot the report manually.",
        );
      }
      opener(message);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const hasContacts = contacts.length > 0;
  const btnStyle = variant === "emerald"
    ? "!bg-emerald-600/20 !border-emerald-500/40 !text-emerald-200 hover:!bg-emerald-600/30"
    : "";
  const btnSize = size === "sm" ? "!text-[11px] !py-1 !px-2.5" : "";

  return (
    <div className="relative" data-no-print="true">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`pill ${btnSize} ${btnStyle}`}
        data-testid="whatsapp-share-report-btn"
        title={hasContacts
          ? `Send this ${reportLabel} to ${athleteName}'s family via WhatsApp`
          : "No family numbers on file — you can still pick a WhatsApp recipient"}
      >
        <MessageCircle size={12} />
        {label}
        <ChevronDown size={10} />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-[400]"
            onClick={() => setOpen(false)}
            data-testid="whatsapp-share-report-backdrop"
          />
          <div
            className="absolute right-0 top-full mt-1 z-[401] w-[300px] rounded-xl border border-emerald-500/30 bg-[#071322] shadow-2xl p-3"
            data-testid="whatsapp-share-report-menu"
          >
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
              Send {reportLabel}
            </div>

            {hasContacts ? (
              <div className="flex flex-col gap-1.5">
                {contacts.map((c, i) => {
                  const meta = ROLE_META[c.role] || ROLE_META.other;
                  const Icon = meta.icon;
                  return (
                    <button
                      key={`${c.mobile}-${i}`}
                      className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-left bg-[#0B1B2E] hover:bg-[#122036] border border-transparent hover:border-emerald-500/40 disabled:opacity-50"
                      disabled={busy}
                      data-testid={`whatsapp-share-report-${c.role}`}
                      onClick={() => doShare((msg) => openWhatsAppTo(c.mobile, msg))}
                    >
                      <Icon size={14} style={{ color: meta.color }} />
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] text-slate-100 leading-tight">
                          {meta.label}
                          {c.name ? <span className="text-slate-400 font-normal"> · {c.name}</span> : null}
                        </div>
                        <div className="text-[11px] text-slate-500 font-mono">+91 {c.mobile}</div>
                      </div>
                    </button>
                  );
                })}
                <div className="border-t border-slate-800 my-1" />
                <button
                  className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-left bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/40 disabled:opacity-50"
                  disabled={busy}
                  data-testid="whatsapp-share-report-pick"
                  onClick={() => doShare((msg) => openWhatsAppPicker(msg))}
                >
                  <MessageCircle size={14} className="text-emerald-300" />
                  <div className="text-[12px] text-emerald-100">
                    Pick recipients in WhatsApp…
                  </div>
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="text-[12px] text-slate-400 leading-snug">
                  No family numbers on file for {athleteName}. Add them from the
                  athlete profile, or run <b>Sync from Attendance</b>.
                </div>
                <button
                  className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/40 disabled:opacity-50"
                  disabled={busy}
                  data-testid="whatsapp-share-report-pick-only"
                  onClick={() => doShare((msg) => openWhatsAppPicker(msg))}
                >
                  <MessageCircle size={14} className="text-emerald-300" />
                  <div className="text-[12px] text-emerald-100">
                    Pick a recipient in WhatsApp
                  </div>
                </button>
              </div>
            )}

            <div className="text-[10px] text-slate-500 mt-2 leading-snug">
              We'll save the report as a PNG in your Photos and open WhatsApp
              with the message ready. Attach the image and send.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
